import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { demoDataUtils } from '@frontend/analytics-shared';

const allLocationsTrend = [46, 50, 48, 30, 55, 60];
const yourLocationTrend = [50, 55, 52, 35, 60, 65];
const newPatientsTrend = [60, 65, 62, 40, 70, 75];
const existingPatientsTrend = [40, 45, 42, 25, 50, 55];
const confirmedStatusTrend = [30, 35, 32, 20, 40, 45];
const unconfirmedStatusTrend = [70, 75, 72, 50, 80, 85];

const averageDifference = (arr1: number[], arr2: number[]) => {
  if (arr1.length !== arr2.length || arr1.length === 0) {
    return 0;
  }

  let sumOfDifferences = 0;
  for (let i = 0; i < arr1.length; i++) {
    sumOfDifferences += arr2[i] - arr1[i];
  }

  return sumOfDifferences / arr1.length;
};

const calculateAverage = (arr: number[]) => {
  if (!arr || arr.length === 0) {
    return 0;
  }

  const sum = arr.reduce((acc, currentValue) => acc + currentValue, 0);
  return sum / arr.length;
};

const last6Months = Array.from({ length: 6 }, (_, i) => i)
  .map((i) => {
    return dayjs()
      .subtract(i + 1, 'month')
      .format('MMM');
  })
  .reverse();

const weaveAverageNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  dates: last6Months.map((month, index) => {
    return {
      key: month,
      params: [
        {
          name: 'AllLocations',
          value: allLocationsTrend[index],
        },
        {
          name: 'SelectedLocations',
          value: yourLocationTrend[index],
        },
      ],
    };
  }),
  previousDateRangeDifference: 2,
  rateDifference: averageDifference(allLocationsTrend, yourLocationTrend),
};

const patientsNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    { name: 'Follow Up Appt', value: calculateAverage(existingPatientsTrend) },
    { name: 'New Appt', value: calculateAverage(newPatientsTrend) },
  ],
  dates: last6Months.map((month, index) => {
    return {
      key: month,
      params: [
        {
          name: 'New Appt',
          value: newPatientsTrend[index],
        },
        {
          name: 'Follow Up Appt',
          value: existingPatientsTrend[index],
        },
      ],
    };
  }),
};

const confirmationStatusNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    { name: 'Confirmed', value: calculateAverage(confirmedStatusTrend) },
    { name: 'Unconfirmed', value: calculateAverage(unconfirmedStatusTrend) },
  ],
  dates: last6Months.map((month, index) => {
    return {
      key: month,
      params: [
        {
          name: 'Confirmed',
          value: confirmedStatusTrend[index],
        },
        {
          name: 'Unconfirmed',
          value: unconfirmedStatusTrend[index],
        },
      ],
    };
  }),
};

const providerNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    { name: 'Dr. Smith', value: 20 },
    { name: 'Dr. Johnson', value: 35 },
    { name: 'Dr. Lee', value: 15 },
    { name: 'Dr. Brown', value: 40 },
    { name: 'Dr. Davis', value: 25 },
    { name: 'Dr. Wilson', value: 30 },
    { name: 'Dr. Taylor', value: 10 },
    { name: 'Dr. Anderson', value: 45 },
  ],
};

const appointmentTypesNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    { name: 'Consultation', value: 25 },
    { name: 'Dental Cleaning', value: 40 },
    { name: 'Dental Surgery', value: 45 },
    { name: 'Emergency', value: 15 },
    { name: 'Follow-up', value: 30 },
    { name: 'Root Canal', value: 50 },
    { name: 'Routine Check-up', value: 20 },
    { name: 'Surgery', value: 10 },
    { name: 'Therapy Session', value: 35 },
  ],
};

const dayWiseNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    { name: 'Monday', value: 30 },
    { name: 'Tuesday', value: 25 },
    { name: 'Wednesday', value: 20 },
    { name: 'Thursday', value: 35 },
    { name: 'Friday', value: 40 },
    { name: 'Saturday', value: 50 },
    { name: 'Sunday', value: 45 },
  ],
};

const allTimeRepeatNSR: AnalyticsGatewayV1.Types.GetNoShowRateIO['output']['data'] = {
  aggregates: [
    {
      name: 'John Doe',
      value: 80,
      id: '1',
      number: '123-456-7890',
      noShowRateCount: 6,
      nextAppointmentDate: '2026-07-15',
    },
    {
      name: 'Jane Smith',
      value: 70,
      id: '2',
      number: '234-567-8901',
      noShowRateCount: 5,
      nextAppointmentDate: '2026-08-20',
    },
    {
      name: 'Alice Johnson',
      value: 75,
      id: '3',
      number: '345-678-9012',
      noShowRateCount: 8,
      nextAppointmentDate: '2026-09-10',
    },
    {
      name: 'Bob Brown',
      value: 65,
      id: '4',
      number: '456-789-0123',
      noShowRateCount: 9,
      nextAppointmentDate: '2026-10-05',
    },
    {
      name: 'Charlie Davis',
      value: 60,
      id: '5',
      number: '567-890-1234',
      noShowRateCount: 3,
      nextAppointmentDate: '2026-11-12',
    },
    {
      name: 'Eve Wilson',
      value: 55,
      id: '6',
      number: '678-901-2345',
      noShowRateCount: 4,
      nextAppointmentDate: '2026-12-01',
    },
    {
      name: 'Frank Miller',
      value: 50,
      id: '7',
      number: '789-012-3456',
      noShowRateCount: 6,
      nextAppointmentDate: '2027-01-18',
    },
    {
      name: 'Grace Lee',
      value: 45,
      id: '8',
      number: '890-123-4567',
      noShowRateCount: 12,
      nextAppointmentDate: '2027-02-15',
    },
  ],
};

export const generateNSRTimelineDemoData = (
  noShowRateType: NoShowRateByType
): AnalyticsGatewayV1.Types.GetNoShowRateIO['output'] => {
  let data = {};

  switch (noShowRateType) {
    case NoShowRateByType.WEAVE_AVERAGE:
      data = weaveAverageNSR;
      break;

    case NoShowRateByType.PATIENT_TYPE:
      data = patientsNSR;
      break;

    case NoShowRateByType.APPOINTMENTS_CONFIRMED:
      data = confirmationStatusNSR;
      break;

    case NoShowRateByType.APPOINTMENTS_PRACTITIONER:
      data = providerNSR;
      break;

    case NoShowRateByType.APPOINTMENTS_EXTERNAL_TYPE:
      data = appointmentTypesNSR;
      break;

    case NoShowRateByType.DAY_WISE:
      data = dayWiseNSR;
      break;

    case NoShowRateByType.APPOINTMENTS_ALL_TIME_PERSONS:
      data = allTimeRepeatNSR;
      break;
  }

  return {
    data,
    message: '',
  };
};

export const generateNoShowRateAggregatesDemoData = (
  locationIds?: string[],
  noShowRateType?: NoShowRateByType
): AnalyticsGatewayV1.Types.GetNoShowRateAggregatesIO['output'] => {
  return {
    data: {
      locations: (locationIds || []).map((locationId) => {
        const aggregates = [];

        switch (noShowRateType) {
          case NoShowRateByType.PATIENT_TYPE:
            aggregates.push(
              {
                name: 'Follow Up Appt',
                value: demoDataUtils.generateRandomInt(30, 50),
              },
              {
                name: 'New Appt',
                value: demoDataUtils.generateRandomInt(40, 60),
              }
            );
            break;

          case NoShowRateByType.APPOINTMENTS_CONFIRMED:
            aggregates.push(
              {
                name: 'Confirmed',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Unconfirmed',
                value: demoDataUtils.generateRandomInt(50, 70),
              }
            );
            break;

          case NoShowRateByType.DAY_WISE:
            aggregates.push(
              {
                name: 'Monday',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Tuesday',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Wednesday',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Thursday',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Friday',
                value: demoDataUtils.generateRandomInt(30, 50),
              },
              {
                name: 'Saturday',
                value: demoDataUtils.generateRandomInt(40, 60),
              },
              {
                name: 'Sunday',
                value: demoDataUtils.generateRandomInt(30, 50),
              }
            );
            break;

          case NoShowRateByType.APPOINTMENTS_EXTERNAL_TYPE:
            aggregates.push(
              {
                name: 'Consultation',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Dental Cleaning',
                value: demoDataUtils.generateRandomInt(30, 50),
              },
              {
                name: 'Dental Surgery',
                value: demoDataUtils.generateRandomInt(40, 60),
              },
              {
                name: 'Emergency',
                value: demoDataUtils.generateRandomInt(10, 30),
              },
              {
                name: 'Follow-up',
                value: demoDataUtils.generateRandomInt(20, 40),
              },
              {
                name: 'Root Canal',
                value: demoDataUtils.generateRandomInt(50, 70),
              },
              {
                name: 'Routine Check-up',
                value: demoDataUtils.generateRandomInt(15, 35),
              }
            );
            break;
        }

        return {
          aggregates,
          locationId,
          noShowRate: demoDataUtils.generateRandomInt(10, 20),
        };
      }),
      unsubscribed: 0.2,
    },
    message: '',
  };
};
